import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuthoringContext } from '../AuthoringContext';
import { MemreFlexBox, MemreText } from 'components/core';
import * as Color from 'core/colors';
import axios from 'axios';
import { normalizeJSONAPIResponse } from 'utils/modelUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { sleep } from 'utils/timeUtils';
import { throttle } from 'lodash';
import Collapse from '@material-ui/core/Collapse';

const StudiableStatus: React.FC = () => {
  const { rawItem, item } = useAuthoringContext();
  const [isStudiable, setIsStudiable] = useState<boolean | null>(null);
  const [previousItem, setPreviousItem] = useState<any>(null);
  const [animationDuration, setAnimationDuration] = useState<number>(300);
  const pollingIDRef = useRef<number>(0);

  const fetchItemStatus = async () => {
    if (!rawItem?.id) return;

    try {
      const response = await axios.get(`/api/v3/items/${rawItem.id}`);
      const data = normalizeJSONAPIResponse(response.data);

      if (data && data.isStudiable !== undefined) {
        rawItem.isStudiable = data.isStudiable;
        setIsStudiable(data.isStudiable);
      }
    } catch (error) {
      console.error('Error fetching item status:', error);
    }
  };

  const throttledPollingSequence = useCallback(
    throttle(
      async () => {
        // Store the current polling ID when starting the polling sequence
        const pollingID = pollingIDRef.current;

        for (let i = 0; i <= 5; i++) {
          await sleep(1000 + 1000 * i);

          if (pollingIDRef.current !== pollingID) {
            return; // This ensures two polling sequences don't happen at the same time
          }

          await fetchItemStatus();
        }
      },
      10000,
      { leading: true, trailing: true }
    ),
    [fetchItemStatus]
  );

  useEffect(() => {
    if (!rawItem) return;
    (async () => {
      setAnimationDuration(0); // No animation for item change
      setIsStudiable(rawItem.isStudiable); // Immediately set isStudiable after item change
      await sleep(500);
      setAnimationDuration(300); // Restore animation duration after a brief pause
    })();
  }, [rawItem?.id]);

  useEffect(() => {
    if (JSON.stringify(item) !== JSON.stringify(previousItem)) {
      setPreviousItem(item);

      pollingIDRef.current += 1; // Increment polling ID to ensure we have a unique ID for this polling sequence
      throttledPollingSequence();
    }
  }, [item, throttledPollingSequence, previousItem]);

  const showNotification = isStudiable === false;

  return (
    <Collapse in={showNotification} timeout={animationDuration} mountOnEnter unmountOnExit>
      <MemreFlexBox
        sx={{
          backgroundColor: Color.veryLightGray,
          padding: 1.5,
          borderRadius: '4px',
          marginBottom: 2,
          border: '1px solid #E0E0E0',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <FontAwesomeIcon
          icon={faInfoCircle}
          style={{
            marginRight: '8px',
            fontSize: '14px',
            color: '#757575',
            opacity: 0.7,
          }}
        />
        <MemreText variant="body2" sx={{ color: '#616161', fontSize: '0.875rem' }}>
          Item is incomplete and will not appear in the learning experience
        </MemreText>
      </MemreFlexBox>
    </Collapse>
  );
};

export default StudiableStatus;
